import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { supabase } from '../../../lib/supabaseClient';
import { formatDate } from '../../../utils/dateFormatter';
import { resolveMergeTags, resolveMergeTagsHtml } from '../../../utils/emailMergeTagResolver';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * EmailPreviewModal - Preview email with merge tags resolved
 *
 * Props:
 * - open: boolean
 * - onOpenChange: Callback(boolean)
 * - template: Email template object
 * - rfqId: UUID of RFQ
 * - candidateId: UUID of candidate for preview (optional)
 */
const EmailPreviewModal = ({ open, onOpenChange, template, rfqId, candidateId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [previewData, setPreviewData] = useState({
    subject: '',
    body: '',
    candidateEmail: null
  });

  useEffect(() => {
    if (open && rfqId) {
      generatePreview();
      checkGmailStatus();
    }
  }, [open, rfqId, candidateId, template]);

  const checkGmailStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/gmail/status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGmailConnected(data.connected);
      }
    } catch (err) {
      console.error('Error checking Gmail status:', err);
    }
  };

  const generatePreview = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch RFQ details
      const { data: rfq, error: rfqError } = await supabase
        .from('rfqs')
        .select('*, client:clients(name)')
        .eq('id', rfqId)
        .single();

      if (rfqError) throw rfqError;

      // Fetch candidate details if provided
      let candidate = null;
      if (candidateId) {
        const { data: candidateData, error: candidateError } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', candidateId)
          .single();

        if (candidateError) throw candidateError;
        candidate = candidateData;
      }

      // Build merge data
      const mergeData = {
        candidate: {
          first_name: candidate?.first_name || '[First Name]',
          last_name: candidate?.last_name || '[Last Name]',
          display_name: candidate?.display_name || '[Display Name]',
          email: candidate?.email || '[Email]'
        },
        rfq: {
          title: rfq.title || '[RFQ Title]',
          ref_code: rfq.ref_code || '[Ref Code]',
          client_name: rfq.client?.name || '[Client Name]',
          locations: Array.isArray(rfq.locations) ? rfq.locations.join(', ') : rfq.locations || '[Locations]',
          clearance_required: rfq.clearance_required || '[Clearance]',
          work_arrangement: rfq.work_arrangement || '[Work Arrangement]',
          estimated_start_date: rfq.estimated_start_date
            ? formatDate(rfq.estimated_start_date, { fallback: '[Start Date]' })
            : '[Start Date]',
          initial_term_months: rfq.initial_term_months || '[Term]',
          description: rfq.description || '[Description]'
        },
        application_url: `${process.env.REACT_APP_SITE_URL || 'https://appsytec.com'}/apply/${rfqId}`
      };

      // Resolve merge tags
      const resolvedSubject = resolveMergeTags(template.subject_template, mergeData);
      const resolvedBodyHtml = resolveMergeTagsHtml(template.body_template, mergeData);

      // Sanitize HTML for preview
      const sanitizedBody = DOMPurify.sanitize(resolvedBodyHtml);

      setPreviewData({
        subject: resolvedSubject,
        body: sanitizedBody,
        candidateEmail: candidate?.email || null
      });
    } catch (err) {
      console.error('Error generating preview:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!previewData.candidateEmail) {
      setError('No candidate email available. Please select a specific candidate.');
      return;
    }

    try {
      setCreatingDraft(true);
      setError(null);
      setSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/gmail/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          to: previewData.candidateEmail,
          subject: previewData.subject,
          body: previewData.body
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create draft');
      }

      const data = await response.json();
      setSuccess('Draft created successfully in your Gmail!');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
      }, 2000);

    } catch (err) {
      console.error('Error creating draft:', err);
      setError(err.message);
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleSendEmail = async () => {
    if (!previewData.candidateEmail) {
      setError('No candidate email available. Please select a specific candidate.');
      return;
    }

    if (!window.confirm(`Send this email to ${previewData.candidateEmail}?`)) {
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/gmail/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          to: previewData.candidateEmail,
          subject: previewData.subject,
          body: previewData.body
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email');
      }

      const data = await response.json();
      setSuccess('Email sent successfully via Gmail!');

      // Auto-close after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
      }, 2000);

    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Email Preview</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <Alert className="mb-4 bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Template Name */}
              <div>
                <p className="text-sm font-medium text-muted-foreground">Template</p>
                <p className="text-sm">{template.name}</p>
              </div>

              {/* Subject */}
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subject</p>
                <p className="text-base font-medium">{previewData.subject}</p>
              </div>

              {/* Body */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Body</p>
                <div className="border rounded-md p-4 bg-muted/30">
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                    {previewData.body}
                  </pre>
                </div>
              </div>

              {/* Note */}
              {!candidateId && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This is a preview with placeholder candidate data. Actual emails will use real candidate information.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t">
          <div className="flex items-center gap-2">
            {!gmailConnected && candidateId && (
              <Alert className="flex items-center gap-2 py-2 px-3">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Connect Gmail in settings to send emails
                </AlertDescription>
              </Alert>
            )}
          </div>
          <div className="flex items-center gap-2">
            {gmailConnected && candidateId && (
              <>
                <Button
                  onClick={handleCreateDraft}
                  disabled={creatingDraft || sending || !previewData.candidateEmail}
                  variant="outline"
                >
                  {creatingDraft ? (
                    <>
                      <Mail className="h-4 w-4 mr-2 animate-pulse" />
                      Creating Draft...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Create Gmail Draft
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSendEmail}
                  disabled={creatingDraft || sending || !previewData.candidateEmail}
                >
                  {sending ? (
                    <>
                      <Send className="h-4 w-4 mr-2 animate-pulse" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send with Gmail
                    </>
                  )}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewModal;
